// Core domain model. All instants are epoch milliseconds (UTC); conversion to the
// airport's local time happens only at the edges (see time.ts).

export type Millis = number;
export type Direction = "ARR" | "DEP";
/** D = domestic, T = transborder (US), I = international */
export type Sector = "D" | "T" | "I";

export type LegStatus =
  | "scheduled"
  | "active" // airborne
  | "landed"
  | "departed"
  | "cancelled"
  | "diverted"
  | "unknown";

/** One flight leg as seen from the airport (an arrival or a departure). */
export interface FlightLeg {
  id: string;
  direction: Direction;
  airline: string; // ICAO or IATA carrier code as supplied
  flightNumber: string; // e.g. "WS3557"
  /** Origin for arrivals, destination for departures (IATA). */
  otherAirport: string | null;
  scheduled: Millis;
  estimated?: Millis | null;
  actual?: Millis | null;
  status: LegStatus;
  /** Set when the status was worked out here, because the flight left the airport feed. */
  statusAssumed?: boolean;
  /** An aircraft parked here with nothing to operate, until a planner gives it a flight. */
  stored?: boolean;
  aircraftType?: string | null; // ICAO type code, e.g. B738
  /**
   * "loads" when nothing said what flew and the type was worked out from the passengers
   * this flight number has carried: still an assumption, shown with a "*".
   */
  aircraftTypeFrom?: "loads" | null;
  registration?: string | null;
  pax?: number | null;
  /** Seats on the aircraft flying this leg, when a schedule source gives it (CopOpt). */
  seats?: number | null;
  /** Checked bags (from load messages / sheets), when known. */
  bags?: number | null;
  /** Where pax/bags came from and when (e.g. "sheet:WestJet loads", "email", "bpss2"). */
  loadSource?: string | null;
  /** Where the bag figure came from, when that is not where the passengers did. */
  bagsSource?: string | null;
  loadUpdatedAt?: Millis | null;
  /** BPSS2: passengers through security for this departure, and those still queuing. */
  screened?: number | null;
  screenedQueued?: number | null;
  sector: Sector;
  codeshares?: string[];
  /** Where this record came from (cirium, ylw-aodb, manual...). */
  source: string;
  /** Resource hints from the feed (e.g. Cirium's gate), used to seed the as-is plan. */
  feedGate?: string | null;
  /**
   * The gate the AODB publishes. Never used to plan: Allocate decides stands itself and the
   * value is often out of date. Only a sandbox reads it, as a labelled fallback where the
   * cameras have nothing for a flight.
   */
  aodbGate?: string | null;
  feedBelt?: string | null;
}

/**
 * A turn is one aircraft visit: arrival, time on the ground, departure.
 * The stand is occupied from on-block to off-block, so allocation works on turns.
 */
export interface Turn {
  id: string;
  arr: FlightLeg | null;
  dep: FlightLeg | null;
  airline: string;
  aircraftType: string | null;
  /** True when aircraftType was guessed (not supplied by the feed). */
  aircraftTypeInferred: boolean;
  registration: string | null;
  sector: Sector;
  /** Planned occupancy (from best-known times: actual > estimated > scheduled). */
  start: Millis;
  end: Millis;
  /** True if start or end had to be assumed (arrival-only or departure-only turn). */
  openStart: boolean;
  openEnd: boolean;
  /** How the arrival and departure were linked (for trust/explainability). */
  linkedBy: "registration" | "flight-number" | "ground-time" | "none" | "planner";
  /**
   * Long turns are split: the aircraft is towed off after the arrival and back before the
   * departure. Each half is allocated on its own; `pairedWith` points at the other half.
   */
  segment?: "arrival" | "departure" | "towed";
  pairedWith?: string;
  /** Remain overnight: on the ground through the night (see remainsOvernight). */
  ron?: boolean;
  /** Mechanical / AOG hold keeping the aircraft on stand until `until`. */
  mech?: { until: Millis; note: string } | null;
  /** For towed segments: the stand the aircraft was towed to (fixed by the planner). */
  towedTo?: string | null;
  /** Label for segments that carry no flight leg (e.g. the middle of a towed visit). */
  displayLabel?: string;
  /**
   * Minutes the departure holds the stand past its off-block time for de-icing, set only
   * when winter operations are on and the hold was applied to this turn.
   */
  deiceMin?: number;
  /**
   * Allocations a planner has held past their usual end, by what is held: the stand, the
   * check-in desks, the reclaim belt or the outbound belt. Something happens that the
   * AODB never publishes - a crew that is late, a bag hall jam - and the duty manager
   * needs the resource for longer than the schedule says. See `Extension`.
   */
  extensions?: Partial<Record<ExtendResource, Extension>>;
}

/** What a planner can hold for longer than the schedule gives it. */
export type ExtendResource = "stand" | "counters" | "belts" | "outbound";

/** One allocation held past its usual end by a planner. */
export interface Extension {
  /** The planner action that made it, so it can be taken back. */
  eventId: string;
  /** The new end. */
  until: Millis;
  /** For the stand, where the stay would have ended without it. */
  from?: Millis;
  note?: string;
  /** Who extended it. */
  by?: string;
}

/** A planner action on an aircraft that changes where or how long it occupies a stand. */
export type OpsEvent =
  | { id: string; kind: "tow"; arrLegId: string | null; depLegId: string | null; at: Millis; toStand: string; note?: string }
  | { id: string; kind: "mech"; arrLegId: string | null; depLegId: string | null; until: Millis; note: string }
  /**
   * A pairing decided here rather than in the feed: a tail swap, or splitting an arrival
   * from its departure. Either side may be empty, which leaves that flight on its own.
   */
  | { id: string; kind: "link"; arrLegId: string | null; depLegId: string | null; note?: string }
  /**
   * The aircraft is not what the feed says. A real change, not a what-if: it holds until
   * someone removes it, and everything downstream - size fit, seats, turn time, the
   * forecast - reads the corrected type.
   */
  | { id: string; kind: "type"; arrLegId: string | null; depLegId: string | null; aircraftType: string; note?: string }
  /**
   * The flight is taken off the board. The feed may keep sending it - a duplicate it
   * will not retract, a charter that was never going to operate - and this outlasts it.
   * Undone from History like any other event. The label is kept because the flight is
   * no longer on the board to be named from.
   */
  | { id: string; kind: "remove"; arrLegId: string | null; depLegId: string | null; label?: string; note?: string }
  /**
   * An allocation held past its usual end: the aircraft stays on its stand, or the desks
   * or belt stay open for the flight, until `until`. Like a maintenance hold it outlasts
   * every re-plan and feed update until someone takes it back. A stand extension names
   * both legs of the visit; a check-in or outbound one the departure; a reclaim one the
   * arrival. `label` keeps the flight's name for History.
   */
  | {
      id: string;
      kind: "extend";
      arrLegId: string | null;
      depLegId: string | null;
      resource: ExtendResource;
      until: Millis;
      note?: string;
      by?: string;
      label?: string;
    }
  /**
   * The flight is cancelled, said here rather than waiting for the feed. One leg per
   * event: cancelling a departure leaves its aircraft's arrival on the board, and the
   * other way round. The leg stays in the day as a cancelled flight - on the C1 row,
   * holding no stand, counter or belt - and comes back when the event is removed.
   *
   * `bags` is set when passengers of a cancelled departure had already checked bags:
   * those bags come back off the aircraft or out of the make-up area, and the
   * passengers collect them at a reclaim belt as if the flight had just arrived.
   */
  | {
      id: string;
      kind: "cancel";
      arrLegId: string | null;
      depLegId: string | null;
      label?: string;
      /** When it was cancelled, and by whom: shown on the C1 row and in the audit. */
      at: Millis;
      by?: string;
      bags?: { from: Millis; to: Millis } | null;
      note?: string;
    };

/** Arrivals and departures paired by hand, which win over whatever the feed implies. */
export interface ForcedLink {
  arr: string | null;
  dep: string | null;
}

export interface OutboundBelt {
  id: string;
  name: string;
  /** Flights that can be worked at the same time. */
  capacity: number;
  /** Empty = any airline. */
  airlines: string[];
  /** Empty = any sector. */
  sectors: Sector[];
  active: boolean;
}

export type StandKind = "contact" | "remote" | "fbo";

export interface Stand {
  id: string;
  name: string;
  gate: string | null;
  kind: StandKind;
  jetbridge: boolean;
  maxWingspan: number;
  /**
   * Customs-cleared gate for international / transborder passengers.
   * "primary" = preferred, "fallback" = allowed with a penalty, null = domestic only.
   */
  international: "primary" | "fallback" | null;
  /** Domestic flights may not use this stand (e.g. a sterile international gate). */
  domesticExcluded: boolean;
  /** Only these airlines may use the stand. Empty or missing = any airline. */
  allowedAirlines?: string[];
  /** Only these aircraft types may use the stand. Empty or missing = any type that fits. */
  allowedTypes?: string[];
  /**
   * False keeps the stand out of automatic allocation: a planner can still put an
   * aircraft there by hand. Used for a stand with its own lead-in line (e.g. 4a).
   */
  autoAllocate?: boolean;
  /**
   * Stands whose aircraft push back into the same space as this one's, so two departures
   * from them close together hold each other up. Empty or missing = the contact stands
   * either side of it in `order`.
   */
  pushbackNeighbours?: string[];
  active: boolean;
  order: number;
}

export interface StandDependency {
  /** When `stand` is occupied by one of `whenTypes` (empty = any type)... */
  stand: string;
  /** ...then `blocks` cannot be used at the same time. */
  blocks: string;
  whenTypes: string[];
  /**
   * Which flights on `stand` set it off: empty is any. A sterile corridor is the case
   * this exists for - at YLW an international or transborder flight on Stand 4 means
   * stands 1 to 3 can take no domestic flight while it is there, because its passengers
   * walk unscreened past those gates to customs.
   */
  whenSectors?: Sector[];
  /** Which flights on `blocks` are kept off: empty is all of them. */
  blocksSectors?: Sector[];
}

export interface StandClosure {
  id: string;
  stand: string;
  from: Millis;
  to: Millis;
  reason: string;
}

export interface AircraftType {
  code: string;
  /** IATA equipment code (Cirium reports equipment in IATA codes, e.g. 73H, DH4). */
  iata?: string | null;
  name: string;
  wingspan: number;
  seats: number;
  minTurnMin: number;
  targetTurnMin: number;
  /**
   * What drives it. Jets board by jet bridge where one is free; turboprops and pistons
   * are ground-loaded. Empty = read from the type code (see `isJet`).
   */
  engine?: "jet" | "turboprop" | "piston";
  /**
   * Minutes a departure of this type spends de-icing on the stand before pushback, used
   * only while winter operations are on. Empty = the airport's `deiceDefaultMin`.
   */
  deiceMin?: number;
}

export interface Airline {
  code: string; // ICAO
  iata: string;
  name: string;
  color: string;
  /** Counters this airline opens per departure, in counter order, e.g. Priority, Check-in, Bag drop. */
  counterLayout?: CounterFunction[];
  /**
   * When this airline's check-in opens and closes, in minutes before the departure time.
   * Airlines differ — a carrier that opens three hours out holds its desks three hours —
   * so these override `settings.counterOpenBeforeMin` / `counterCloseBeforeMin`.
   */
  counterOpenBeforeMin?: number;
  counterCloseBeforeMin?: number;
  /**
   * Check-in for this airline's US (transborder) departures, when it is run apart from its
   * domestic check-in: WestJet's US flights check in on common-use counters signed WS-USA,
   * with the priority desk WS-USA-PRIORITY, while its own counters carry on with domestic.
   * Set, a transborder departure takes common-use counters in this layout whatever counters
   * the airline holds; unset, it checks in like any other of the airline's flights.
   */
  transborderCheckIn?: TransborderCheckIn;
  /**
   * An https link to the airline's logo, pasted by an administrator. Pages fall back to a
   * tile in the airline's colour with its code when there is none or it fails to load.
   */
  logoUrl?: string;
}

export interface TransborderCheckIn {
  /** What the counters are signed, e.g. "WS-USA". The priority desk adds "-PRIORITY". */
  label: string;
  /** Counters per US departure, in counter order. Empty = by seats, like any airline. */
  counterLayout?: CounterFunction[];
}

/** What a check-in counter is used for while a flight is open. */
export type CounterFunction = "priority" | "checkin" | "bagdrop" | "special";

export interface Counter {
  id: string;
  number: number;
  /** Airline that holds this counter permanently (standing assignment), if any. */
  dedicatedTo: string | null;
  /** A fixed use for this counter (e.g. always Priority). Empty = set per flight. */
  function?: CounterFunction | null;
}

export interface Belt {
  id: string;
  name: string;
  /** Number of flight positions that can run at once. */
  capacity: number;
  /**
   * Which sectors may use this belt. Empty or missing = any. A belt inside the customs
   * hall is set to International, or International and Transborder where both clear
   * there; the flights it is set for also prefer it over a general belt, and it is kept
   * clear while one of them is due.
   */
  sectors?: Sector[];
  /**
   * True when *only* those sectors may use it, for a belt physically inside the customs
   * hall where domestic bags cannot go. False, the usual case, means the belt is kept
   * clear while one of its sectors is due and is otherwise shared like any other.
   */
  exclusive?: boolean;
  /** Older shape, read once and turned into `sectors` and `exclusive`. */
  international?: "only" | "preferred" | null;
  /** Empty or missing = any airline. */
  allowedAirlines?: string[];
}

/** A soft preference: airline X prefers stands matching the predicate. */
export interface StandPreference {
  id: string;
  label: string;
  airlines: string[];
  /** Specific flight numbers this applies to (either direction). Empty = the airlines above. */
  flights?: string[];
  /**
   * Narrow it to what the airline is flying. A Q400 boards by stairs and does not need
   * the bridge its 737 does, so one airline can carry two rules: jets to the bridges,
   * turboprops to the ground-loaded stands, keeping the bridges for what needs them.
   * `aircraftTypes` applies only to those types; `exceptAircraftTypes` to all but those.
   * Both empty: every aircraft the airline flies.
   */
  aircraftTypes?: string[];
  exceptAircraftTypes?: string[];
  /** `jetbridge: false` is itself a preference: the ground-loaded stands, on purpose. */
  prefer: { jetbridge?: boolean; stands?: string[] };
  weight: number;
  /** True for a preference worked out from past plans rather than written by a planner. */
  learned?: boolean;
}

export interface Settings {
  /** Minimum gap between two turns on the same stand (hard). */
  minBufferMin: number;
  /** Comfortable gap; smaller gaps cost a little (soft). */
  preferredBufferMin: number;
  /** Assumed ground time for an arrival with no linked departure. */
  defaultTurnMin: number;
  /** Turns longer than this are split into arrival + departure with a tow to parking between. 0 = never (RON aircraft stay parked on stand). */
  towSplitMin: number;
  outboundOpenBeforeMin?: number;
  outboundCloseBeforeMin?: number;
  /** Conflicts further out than this are fixed automatically; closer ones ask for confirmation by text. */
  autoResolveWindowMin?: number;
  autoResolveEnabled?: boolean;
  /** Most conflict questions texted in any hour; past it, one text says how many more wait. 0 = no limit. Default 6. */
  autoResolveTextsPerHour?: number;
  /** Keep international gates free when international flights are due within this window. */
  /** Follow the stands planners chose by hand on past days, as a soft preference. */
  learnFromHistory?: boolean;
  /** How strongly to follow them: parking somewhere else costs twice this. */
  learnedWeight?: number;
  intlLookaheadMin: number;
  intlProtectionPenalty: number;
  intlFallbackPenalty: number;
  remotePenalty: number;
  /**
   * Cost of a jet on a stand with no jet bridge, for every airline. Heavier than the
   * airline and learned preferences together, far lighter than an aircraft on no stand,
   * so a jet goes to a bridge whenever one is free and still gets a stand when none is.
   */
  jetNoBridgePenalty?: number;
  /**
   * Two departures from neighbouring stands within this many minutes of each other: the
   * second push is held for the first. 0 = not considered.
   */
  pushbackSpacingMin?: number;
  /** Cost of each such pair. A nice-to-have, so it breaks ties rather than overriding preferences. */
  pushbackPenalty?: number;
  /**
   * The customs walk. An international or transborder arrival's passengers walk past the
   * other customs gates to the customs hall for this many minutes after it arrives. 0 = off.
   */
  customsClearMin?: number;
  /** A domestic arrival on another customs gate must be at least this many minutes earlier. */
  customsDomesticBeforeMin?: number;
  /** Boarding: the minutes before a departure that its passengers are at the gate. */
  boardingMin?: number;
  /**
   * Cost of passengers meeting on the customs walk. Below an aircraft with no stand, so the
   * gate is still used when nothing else is free; and every such case is a conflict sent
   * for approval.
   */
  customsWalkPenalty?: number;
  /**
   * Bridges go to jets first, then to Q400s. Cost of a turboprop on a jet bridge while a
   * jet that could use that bridge, on the ground at the same time, is on a stand without one.
   */
  bridgePriorityPenalty?: number;
  /** A turboprop on the ground longer than this many minutes should not hold a jet bridge. */
  longPropOnBridgeMin?: number;
  /** Cost of a turboprop holding a jet bridge for longer than longPropOnBridgeMin. */
  longPropOnBridgePenalty?: number;
  /** No longer used: large jets arriving together are not spread apart. Kept so saved settings still load. */
  jetArrivalSpacingMin?: number;
  /** No longer used, as jetArrivalSpacingMin. */
  jetSpreadPenalty?: number;
  /**
   * Keeping an airline's aircraft together, for the airlines in keepTogetherAirlines only:
   * cost per gate between two of its aircraft on the ground at the same time.
   */
  handlerSpreadPenalty?: number;
  /** The airlines (ICAO) whose aircraft are kept together. YLW: Air Canada and WestJet. */
  keepTogetherAirlines?: string[];
  /** Keeping a ground handler's aircraft close: cost per gate between. A very low priority. */
  handlerTogetherPenalty?: number;
  /** Cost of an aircraft away from the stands where its ground handler keeps its equipment. */
  handlerHomePenalty?: number;
  /**
   * A jet on the ground longer than this many minutes, other than overnight from the evening
   * to the early morning, is better on a ground-load stand than holding a bridge.
   */
  jetLongStayMin?: number;
  /** Cost of such a long-stay jet on a jet bridge. */
  jetLongStayOnBridgePenalty?: number;
  /** Small turboprops, by type code, kept off jet bridge gates (the Beech 1900, Saab 340, Metro). */
  smallPropTypes?: string[];
  /** Cost of a small turboprop on a jet bridge gate. */
  smallPropOnBridgePenalty?: number;
  /** Penalty per minute of buffer below preferredBufferMin. */
  tightBufferPenaltyPerMin: number;
  /** Penalty for putting a small aircraft on a stand much bigger than it needs. */
  oversizePenalty: number;
  counterOpenBeforeMin: number;
  counterCloseBeforeMin: number;
  beltStartAfterMin: number;
  beltDurationMin: number;
  /**
   * A gap kept after a flight's bags before the belt counts as free again: the last bags
   * are collected well after the belt stops. The same idea as the buffer between two
   * aircraft on a stand.
   */
  beltBufferMin?: number;
  /**
   * International and transborder bags sit on the belt longer, because the passengers
   * are clearing customs before they reach them. Empty = the ordinary duration.
   */
  beltDurationIntlMin?: number;
  /**
   * Winter operations. At YLW an aircraft is de-iced on its stand before pushback, so
   * while this is on every departure holds its stand that much longer than its departure
   * time: the type's own figure, or `deiceDefaultMin` where the type has none. Absent is
   * off, so an airport saved before the setting existed plans as it always did.
   */
  deicingEnabled?: boolean;
  /** Minutes of de-icing assumed for a type with no figure of its own. Absent = 15. */
  deiceDefaultMin?: number;
  /**
   * How many days of schedule, from today, are read from CopOpt when it is connected.
   * The airport feed holds today and tomorrow; CopOpt's schedule reaches further, and is
   * the first source for the days beyond the feed. Absent = 7.
   */
  copoptScheduleDays?: number;
  /**
   * How many days beyond today are planned by the hourly plan-ahead job, so a day's
   * first plan exists before anyone opens it and a flight that arrives later is placed.
   * 0 switches the job off. Absent = 7.
   */
  planAheadDays?: number;
  /**
   * How long the stills and clips Vectro Apron sends are kept, in days. They are the
   * heaviest thing the app stores and are only looked at while a day is fresh. Absent = 14.
   */
  apronMediaDays?: number;
  /** How long Vectro Apron's turn status (state, steps, registration check) is kept, in days. Absent = 90. */
  apronTurnDays?: number;
  /**
   * The most the camera stills and clips may take up altogether, in MB. Past it the oldest
   * go first, so a burst of uploads can never fill the database the plans live in. Absent = 2048.
   */
  apronMediaMaxMb?: number;
  /**
   * Say so on the board and in the flight panel when the cameras see an aircraft somewhere the
   * plan does not have it. Off by default: this plan follows its own rules, so a difference
   * from the cameras is expected here and not a problem to raise.
   */
  apronShowPlanDifferences?: boolean;
}

/**
 * Days of schedule read from CopOpt when Settings holds no figure: the whole fortnight a
 * future day can be planned, so none of it falls back to past weekdays while CopOpt
 * publishes it. CopOpt publishes YLW's schedule more than two weeks out.
 */
export const COPOPT_SCHEDULE_DAYS_DEFAULT = 14;
/** Days beyond today the plan-ahead job keeps planned when Settings holds no figure. */
export const PLAN_AHEAD_DAYS_DEFAULT = 7;
/** Days camera stills and clips are kept when Settings holds no figure. */
export const APRON_MEDIA_DAYS_DEFAULT = 14;
/** Days Vectro Apron's turn status is kept when Settings holds no figure. */
export const APRON_TURN_DAYS_DEFAULT = 90;
/** MB all camera stills and clips may take up together when Settings holds no figure. */
export const APRON_MEDIA_MAX_MB_DEFAULT = 2048;

/** The security checkpoint: what there is to open, and what a lane gets through. */
export interface SecurityConfig {
  /** Checkpoint name as the screening feed reports it, e.g. "PBS". */
  checkpoint: string;
  /** Lanes the checkpoint physically has. */
  lanes: number;
  /** Passengers one lane clears per hour. Null or absent = use what we have measured. */
  perLaneHour?: number | null;
  /** The wait the airport plans to: this many minutes for the share below. */
  targetWaitMin?: number;
  /** Share of passengers who should be within the target, e.g. 0.85. */
  targetWaitShare?: number;
}

export interface AirportConfig {
  code: string; // ICAO, e.g. CYLW
  iata: string;
  name: string;
  timezone: string;
  stands: Stand[];
  dependencies: StandDependency[];
  counters: Counter[];
  belts: Belt[];
  outboundBelts?: OutboundBelt[];
  /** Where passenger / bag loads come from (Google Sheets polled on a schedule). */
  loadSources?: { sheets: Array<{ id: string; name: string; url: string; enabled: boolean; airline?: string | null }>; pollMinutes: number };
  aircraftTypes: Record<string, AircraftType>;
  airlines: Record<string, Airline>;
  /** Maps carrier codes (IATA or ICAO) to a canonical ICAO code. */
  airlineAliases: Record<string, string>;
  preferences: StandPreference[];
  /** Default aircraft type per airline when the feed has none (used as a labelled guess). */
  defaultTypeByAirline: Record<string, string>;
  /** More specific guesses by flight-number range, checked before defaultTypeByAirline. */
  defaultTypeRules?: Array<{ airline: string; from: number; to: number; type: string; note?: string }>;
  /** The security checkpoint, for the passenger forecast. */
  security?: SecurityConfig;
  /**
   * The airport's own networks, so a sign-in from an address can be read as a place -
   * "the terminal", "the ops office" - instead of a number. Nothing is sent anywhere to
   * work this out; an address that matches nothing is simply shown as it is.
   */
  networks?: Array<{ name: string; prefix: string }>;
  /**
   * The ground handlers and the airlines each works for. Each keeps its equipment by some
   * gates, so its airlines are planned together and near them.
   */
  groundHandlers?: GroundHandler[];
  settings: Settings;
}

export interface GroundHandler {
  id: string;
  name: string;
  /** Airlines it handles, ICAO codes. Empty: every airline no other handler names. */
  airlines: string[];
  /** Stands where its equipment is kept; empty when it works anywhere. */
  homeStands: string[];
}

/** A stand assignment for one turn. */
export interface StandAssignment {
  turnId: string;
  standId: string | null;
  locked: boolean;
  /**
   * Set when a person chose this stand. The app repairs its own placements when the day
   * changes underneath them; it does not quietly undo a planner's decision, whether or not
   * they locked it.
   */
  by?: "planner" | "cameras" | "aodb";
}

export interface CounterAssignment {
  legId: string;
  counters: string[];
  /** What each counter is used for on this flight. */
  roles?: Record<string, CounterFunction>;
  /** What each counter is signed for this flight, when the airline signs them: "WS-USA". */
  labels?: Record<string, string>;
  from: Millis;
  to: Millis;
  /** Set when a planner extended the desks: where they would have closed without it. */
  extendedFrom?: Millis;
}

export interface BeltAssignment {
  legId: string;
  beltId: string | null;
  from: Millis;
  to: Millis;
  /** Set when a planner extended the belt: where it would have ended without it. */
  extendedFrom?: Millis;
}

export interface Plan {
  stands: Record<string, StandAssignment>; // by turnId
  counters: Record<string, CounterAssignment>; // by departure legId
  /** By arrival legId (reclaim), and by the legId of a cancelled departure whose bags come back. */
  belts: Record<string, BeltAssignment>;
  outbound?: Record<string, BeltAssignment>; // by departure legId (make-up)
}

/** Planner overrides for counters and belts, kept with the plan version. */
export interface ResourceOverrides {
  counters?: Record<string, string[]>;
  belts?: Record<string, string | null>;
  outbound?: Record<string, string | null>;
}

export type ConflictKind =
  | "overlap"
  | "buffer"
  | "size"
  | "international"
  | "dependency"
  | "closure"
  | "inactive"
  | "unallocated"
  | "counter-overlap"
  | "belt-capacity"
  | "outbound-capacity"
  | "jet-no-bridge"
  | "pushback"
  | "customs-walk";

export interface Conflict {
  id: string;
  kind: ConflictKind;
  severity: "hard" | "warning";
  resource: string | null;
  turnIds: string[];
  at: Millis;
  message: string;
}

export interface PlanScore {
  /** 0–100, higher is better. */
  quality: number;
  hardConflicts: number;
  warnings: number;
  unallocated: number;
  contactPct: number;
  jetbridgePaxPct: number;
  preferenceMet: number;
  preferenceTotal: number;
  minBufferMin: number | null;
  tightTurns: number;
  cost: number;
}
